import React, { useState, useEffect, useRef } from 'react';
import { config } from '../../config/appConfig';

const TRACK_COLORS = [
    '#a855f7', // purple
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#f97316', // orange
    '#10b981', // emerald
    '#6366f1', // indigo
    '#f43f5e', // rose
    '#eab308', // yellow
];

const DonutChart = ({ tracks, totalPlays, size = 140, strokeWidth = 16 }) => {
    const center = size / 2;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;

    let cumulativePercent = 0;
    const segments = tracks.map((track, i) => {
        const percent = totalPlays > 0 ? track.totalPlays / totalPlays : 0;
        const dashArray = `${circumference * percent} ${circumference * (1 - percent)}`;
        const dashOffset = -circumference * cumulativePercent;
        cumulativePercent += percent;
        return {
            ...track,
            color: TRACK_COLORS[i % TRACK_COLORS.length],
            dashArray,
            dashOffset,
            percent,
        };
    });

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
                {/* Background ring */}
                <circle
                    cx={center} cy={center} r={radius}
                    fill="none" stroke="rgba(255,255,255,0.03)" strokeWidth={strokeWidth}
                />
                {/* Segments */}
                {segments.map((seg, i) => (
                    <circle
                        key={i}
                        cx={center} cy={center} r={radius}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth={strokeWidth}
                        strokeDasharray={seg.dashArray}
                        strokeDashoffset={seg.dashOffset}
                        strokeLinecap="round"
                        className="transition-all duration-700"
                        style={{ opacity: 0.75 }}
                    />
                ))}
            </svg>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-black text-white">{totalPlays}</span>
                <span className="text-[9px] text-gray-500 uppercase tracking-wider">plays</span>
            </div>
        </div>
    );
};

const MiniGauge = ({ value, label, color, max = 100 }) => {
    const size = 56;
    const strokeWidth = 5;
    const center = size / 2;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const percent = Math.min(value / max, 1);
    const dashArray = `${circumference * percent} ${circumference * (1 - percent)}`;

    return (
        <div className="flex flex-col items-center gap-1">
            <div className="relative" style={{ width: size, height: size }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
                    <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth={strokeWidth} />
                    <circle
                        cx={center} cy={center} r={radius} fill="none"
                        stroke={color} strokeWidth={strokeWidth}
                        strokeDasharray={dashArray} strokeDashoffset={0}
                        strokeLinecap="round" style={{ opacity: 0.7 }}
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-300">{value}%</span>
                </div>
            </div>
            <span className="text-[9px] text-gray-600 uppercase tracking-wider">{label}</span>
        </div>
    );
};

const MusicStatsDev = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [hoveredTrack, setHoveredTrack] = useState(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(config.api.analytics.musicStats);
                const result = await response.json();
                if (result.success && result.data && result.data.tracks) {
                    setStats(result.data);
                }
            } catch (e) {
                console.error('Failed to load dev music stats', e);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading || !stats || !stats.tracks || stats.tracks.length === 0) {
        return null;
    }

    const totalPlays = stats.tracks.reduce((sum, t) => sum + t.totalPlays, 0);

    return (
        <div className="max-w-7xl mx-auto px-4 mt-4 mb-16 opacity-20 hover:opacity-100 transition-opacity duration-500">
            <div className="bg-[#080808] border border-white/[0.04] rounded-2xl p-5">
                {/* Header */}
                <div className="flex items-center gap-2 mb-5">
                    <i className="fas fa-terminal text-[10px] text-gray-600"></i>
                    <span className="text-gray-500 font-mono text-[10px] uppercase tracking-[0.2em]">
                        dev · music · 24h
                    </span>
                </div>

                {/* Main content: donut + legend + gauges */}
                <div className="flex flex-col md:flex-row items-center gap-6">
                    {/* Donut */}
                    <div className="shrink-0">
                        <DonutChart tracks={stats.tracks} totalPlays={totalPlays} />
                    </div>

                    {/* Legend */}
                    <div className="flex-1 min-w-0">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
                            {stats.tracks.map((track, i) => {
                                const color = TRACK_COLORS[i % TRACK_COLORS.length];
                                const percent = totalPlays > 0 ? ((track.totalPlays / totalPlays) * 100).toFixed(1) : 0;
                                const isHovered = hoveredTrack === i;
                                return (
                                    <div
                                        key={i}
                                        className={`flex items-center gap-2 py-1 px-2 rounded-md cursor-default transition-all duration-150 ${isHovered ? 'bg-white/[0.03]' : ''}`}
                                        onMouseEnter={() => setHoveredTrack(i)}
                                        onMouseLeave={() => setHoveredTrack(null)}
                                    >
                                        <div
                                            className="w-2 h-2 rounded-full shrink-0"
                                            style={{ backgroundColor: color }}
                                        />
                                        <span className="text-[11px] text-gray-400 truncate flex-1 font-mono">
                                            {track.trackName}
                                        </span>
                                        <span className="text-[11px] text-gray-600 font-mono shrink-0">
                                            {track.totalPlays}
                                            <span className="text-gray-700 ml-0.5">({percent}%)</span>
                                        </span>
                                        {/* Tooltip on hover */}
                                        {isHovered && (
                                            <div className="absolute z-50 mt-20 ml-4 bg-[#111] border border-white/10 rounded-lg p-3 shadow-xl pointer-events-none">
                                                <div className="text-[10px] font-mono text-gray-400 space-y-1">
                                                    <div>Stay: <span className="text-emerald-400">{track.stayRate}%</span></div>
                                                    <div>Mute: <span className="text-orange-400">{track.muteRate}%</span></div>
                                                    <div>Avg mute: <span className="text-gray-300">{track.avgSecondsToMute}s</span></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Mini gauges */}
                    <div className="flex md:flex-col gap-4 shrink-0">
                        <MiniGauge value={stats.stayRate} label="Stay" color="#10b981" />
                        <MiniGauge value={stats.muteRate} label="Mute" color="#f97316" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MusicStatsDev;
